import type { BackendTypes } from '@tf/api';
import {
	Box,
	Button,
	Group,
	Icon,
	Input,
	Loader,
	Modal,
	ScrollArea,
	Stack,
	TFNotifier,
	TFSection,
	TFText,
	useDisclosure,
} from '@tf/ui';
import type React from 'react';
import { useEffect, useState } from 'react';

import type { AdhocSearchParams } from '../../../../api/backgroundCheck';
import {
	useCreateBackgroundCheckMutation,
	useLatestBackgroundCheck,
	useUpdateBackgroundCheckMatchesMutation,
} from '../../../../api/backgroundCheck';
import { useConfigContext, useSegmentContext } from '../../../../hooks';

import { EditSearchParamsButton } from './EditSearchParamsButton';
import { JobResultCard } from './JobResultCard';
import { JobResultCommentsView } from './JobResultComments';
import { JobResultTableContainer } from './JobResultTable';

const isIndividual = (
	additionalData: BackendTypes.AdhocSearchEntityParams | BackendTypes.AdhocSearchPersonParams,
): additionalData is BackendTypes.AdhocSearchPersonParams => {
	if (additionalData) {
		return 'firstName' in additionalData;
	}

	return false;
};

interface Props {
	isReadOnly: boolean;
}

export const BackgroundCheck: React.FC<Props> = ({ isReadOnly }) => {
	const {
		identity: { graphId: unitNodeId },
	} = useSegmentContext();
	const { additionalData } = useConfigContext((s) => s);

	const [searchParams, setSearchParams] = useState<AdhocSearchParams>(() => {
		if (!additionalData) {
			return {};
		} else if (!isIndividual(additionalData)) {
			return { entity: additionalData };
		} else {
			return { person: additionalData };
		}
	});

	const latestBackgroundCheck = useLatestBackgroundCheck(unitNodeId);
	const latestJobData = latestBackgroundCheck.data?.data;

	const resultsModal = useDisclosure();
	const [createdJobId, setCreatedJobId] = useState<string | null>(null);

	const createBackgroundCheckMutation = useCreateBackgroundCheckMutation();
	const isSearching =
		createBackgroundCheckMutation.isPending ||
		latestBackgroundCheck.data?.meta['@status'] === 'PENDING' ||
		latestBackgroundCheck.data?.meta['@status'] === 'IN_PROGRESS';

	const updateBackgroundCheckMatchesMutation = useUpdateBackgroundCheckMatchesMutation();

	useEffect(() => {
		const isWaitingForCreatedJob = createdJobId !== null && createdJobId === latestBackgroundCheck.data?.id;

		if (isWaitingForCreatedJob) {
			if (latestBackgroundCheck.data?.meta['@status'] === 'SUCCESS') {
				resultsModal.open();
			} else if (latestBackgroundCheck.data?.meta['@status'] === 'FAILED') {
				TFNotifier.error('Could not perform search. Please try again later.');
			}
		}
	}, [latestBackgroundCheck.data?.id, latestBackgroundCheck.data?.meta['@status'], resultsModal.open, createdJobId]);

	// TODO: why? probably remove
	if (!additionalData && !isReadOnly) {
		return null;
	}

	const onSearch = async () => {
		createBackgroundCheckMutation.mutate(
			{
				unitNodeId,
				adhocSearchParams: searchParams,
			},
			{
				onSuccess: (data) => {
					setCreatedJobId(data.id);
				},
				onError: () => {
					TFNotifier.error('Could not perform search. Please try again later.');
				},
			},
		);
	};

	const saveMatches = async (matches: { ids: string[]; comments: BackendTypes.AdhocComment[] | null }) => {
		if (!latestBackgroundCheck.data?.id) {
			throw new Error("Can't update matches without latest background check data");
		}
		updateBackgroundCheckMatchesMutation.mutate(
			{
				unitNodeId,
				unitAdhocSearchId: latestBackgroundCheck.data.id,
				matches,
			},
			{ onSuccess: resultsModal.close },
		);
	};

	return (
		<TFSection
			title={<Input.Label m={0}>Background check</Input.Label>}
			icon={<Icon.IconListSearch size={18} />}
			actions={
				!isReadOnly ? (
					<EditSearchParamsButton
						// TODO: why we need this then? Ask whether we need to show params in readonly mode or not
						isReadOnly={isReadOnly}
						initialValue={searchParams}
						onSubmit={setSearchParams}
						isIndividual={isIndividual(additionalData)}
					/>
				) : null
			}
		>
			<Stack gap="md">
				{/* No search */}
				{isReadOnly && !latestJobData?.result?.searchId ? (
					<Box
						sx={({ colors, radius }) => ({
							display: 'flex',
							width: '100%',
							alignItems: 'center',
							justifyContent: 'space-between',
							padding: '.75rem .75rem',
							borderRadius: radius.sm,
							backgroundColor: colors.gray[1],
						})}
					>
						<Group
							sx={({ colors }) => ({
								flex: 1,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								marginRight: '.5rem',
								cursor: 'default',
								color: colors.dark[7],
							})}
						>
							<TFText size="sm" lineClamp={1}>
								Search was not performed for this account
							</TFText>
						</Group>
					</Box>
				) : null}

				{/* Selected matches */}
				{latestJobData?.selectedMatchIds?.map((matchId) => (
					<JobResultCard
						key={matchId}
						hit={latestJobData?.result?.rawResponse.hits.find((hit) => hit.doc.id === matchId)}
					/>
				))}

				{/* No selected matches - show comments */}
				{latestJobData?.result?.searchId && !latestJobData.selectedMatchIds?.length ? (
					<JobResultCommentsView comments={latestJobData?.comments ?? []} />
				) : null}

				{/* Search button */}
				{isReadOnly ? null : latestJobData?.result?.searchId ? (
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<Group gap="0.25rem">
							<Icon.IconExclamationCircle size={14} />
							<TFText c="gray">New search will discard existing results</TFText>
						</Group>
						<Button variant="light" disabled={isSearching} onClick={onSearch}>
							New search
						</Button>
					</Box>
				) : (
					<Button variant="light" disabled={isSearching} onClick={onSearch}>
						<TFText inherit>
							{isSearching ? (
								<Group>
									<Loader size={18} color="gray" />
									Searching...
								</Group>
							) : (
								'Search'
							)}
						</TFText>
					</Button>
				)}
			</Stack>

			<Modal
				title="Background check"
				opened={resultsModal.isOpen}
				size={900}
				withinPortal
				scrollAreaComponent={ScrollArea.Autosize}
				closeOnClickOutside={false}
				onClose={resultsModal.close}
			>
				<JobResultTableContainer
					searchName={
						searchParams.entity?.entityName || `${searchParams.person?.firstName} ${searchParams.person?.lastName}`
					}
					searchId={latestJobData?.result?.searchId}
					hits={latestJobData?.result?.rawResponse.hits ?? []}
					onSaveMatches={saveMatches}
				/>
			</Modal>
		</TFSection>
	);
};
